// ============================================================================
// ADMIN MANAGER SERVICE
// ============================================================================

// Use environment variable instead of @types import
const API_BASE_URL = import.meta.env.VITE_API_URL;

// ============================================================================
// TYPES
// ============================================================================

export interface AdminProductUpdate {
  /** Calendar date `YYYY-MM-DD`, or null to clear expiry. Matches services UpdateUserProductHandler. */
  validUntil?: string | null;
}

export interface AdminCourseUpdate {
  current_section: number;
  current_time: number;
  completed_at?: string;
  passed_at?: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Update product validity date
 */
export const updateUserProduct = async (
  userId: string | number,
  productId: number,
  updateData: AdminProductUpdate
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/products/${productId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error updating user product:', error);
    throw error;
  }
};

/**
 * Remove product from user
 */
export const removeUserProduct = async (
  userId: string | number,
  productId: number
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/products/${productId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-type': 'application/json; charset=UTF-8',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error removing user product:', error);
    throw error;
  }
};

/**
 * Update user course progress
 */
export const updateUserCourse = async (
  userId: string | number,
  courseId: number,
  updateData: AdminCourseUpdate
): Promise<boolean> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/services/admin/users/${userId}/courses/${courseId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return true;
  } catch (error) {
    console.error('❌ Error updating user course:', error);
    throw error;
  }
};

// ============================================================================
// SAFE WRAPPERS
// ============================================================================

/**
 * Safe wrapper for updateUserProduct with error handling
 */
export const updateUserProductSafe = async (
  userId: string | number,
  productId: number,
  updateData: AdminProductUpdate
): Promise<boolean> => {
  try {
    return await updateUserProduct(userId, productId, updateData);
  } catch (error) {
    console.error('❌ Safe updateUserProduct failed:', error);
    throw error; // Re-throw to allow proper error handling in the component
  }
};

/**
 * Safe wrapper for removeUserProduct with error handling
 */
export const removeUserProductSafe = async (
  userId: string | number,
  productId: number
): Promise<boolean> => {
  try {
    return await removeUserProduct(userId, productId);
  } catch (error) {
    console.error('❌ Safe removeUserProduct failed:', error);
    throw error; // Re-throw to allow proper error handling in the component
  }
};

/**
 * Safe wrapper for updateUserCourse with error handling
 */
export const updateUserCourseSafe = async (
  userId: string | number,
  courseId: number,
  updateData: AdminCourseUpdate
): Promise<boolean> => {
  try {
    return await updateUserCourse(userId, courseId, updateData);
  } catch (error) {
    console.error('❌ Safe updateUserCourse failed:', error);
    throw error; // Re-throw to allow proper error handling in the component
  }
};
